<template>
  <div class="youtube-player-shell">
    <div ref="playerMount" class="youtube-player-mount"></div>
    <div v-if="loading" class="youtube-player-loading" role="status">
      <span class="youtube-player-spinner" aria-hidden="true"></span>
      <span>{{ $t('common.loading') }}</span>
    </div>
  </div>
</template>

<script>
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'

let youtubeApiPromise = null

// Time reports still in flight when a seek is issued can run slightly past the old position.
const STALE_TIME_BEHIND = 0.05
const STALE_TIME_AHEAD = 0.5

function loadYouTubeApi() {
  if (window.YT?.Player) return Promise.resolve(window.YT)
  if (youtubeApiPromise) return youtubeApiPromise

  youtubeApiPromise = new Promise((resolve, reject) => {
    const previousReady = window.onYouTubeIframeAPIReady
    window.onYouTubeIframeAPIReady = () => {
      previousReady?.()
      resolve(window.YT)
    }

    const existingScript = document.querySelector('script[src="https://www.youtube.com/iframe_api"]')
    if (existingScript) return

    const script = document.createElement('script')
    script.src = 'https://www.youtube.com/iframe_api'
    script.async = true
    script.onerror = () => reject(new Error('Unable to load the YouTube player'))
    document.head.appendChild(script)
  })

  return youtubeApiPromise
}

function createMediaAdapter(player, eventTarget, state) {
  const dispatch = (name, detail = null) => {
    const event = new CustomEvent(name, { detail })
    eventTarget.dispatchEvent(event)
  }

  // The iframe reports its time asynchronously, so right after seekTo() the player
  // still returns the position it was at before. Until the reported time leaves that
  // old position, answer with the seek target, like HTMLMediaElement does.
  let pendingSeek = null
  const isNearSeekOrigin = (time, origin) => time >= origin - STALE_TIME_BEHIND && time <= origin + STALE_TIME_AHEAD

  return {
    get currentTime() {
      const reported = Number(player.getCurrentTime?.() || 0)
      if (!pendingSeek) return reported
      if (isNearSeekOrigin(reported, pendingSeek.origin)) return pendingSeek.target
      pendingSeek = null
      return reported
    },
    set currentTime(value) {
      const target = Number(value) || 0
      const origin = Number(player.getCurrentTime?.() || 0)
      pendingSeek = isNearSeekOrigin(target, origin) ? null : { target, origin }
      dispatch('seeking')
      player.seekTo?.(target, true)
      window.setTimeout(() => {
        dispatch('seeked')
        dispatch('timeupdate')
      }, 80)
    },
    get duration() {
      return Number(player.getDuration?.() || 0)
    },
    get volume() {
      return Number(player.getVolume?.() || 100) / 100
    },
    set volume(value) {
      player.setVolume?.(Math.round(Math.max(0, Math.min(1, Number(value))) * 100))
    },
    get playbackRate() {
      return Number(player.getPlaybackRate?.() || 1)
    },
    set playbackRate(value) {
      player.setPlaybackRate?.(Number(value) || 1)
    },
    // Like HTMLMediaElement: play()/pause() change it immediately, so a pause issued
    // while YouTube is still buffering is not skipped as "already paused".
    get paused() {
      return !state.playing
    },
    get ended() {
      return state.value === window.YT?.PlayerState?.ENDED
    },
    get readyState() {
      return state.ready ? 4 : 0
    },
    play() {
      state.playing = true
      player.playVideo?.()
      return Promise.resolve()
    },
    pause() {
      state.playing = false
      player.pauseVideo?.()
    },
    addEventListener(name, handler) {
      eventTarget.addEventListener(name, handler)
    },
    removeEventListener(name, handler) {
      eventTarget.removeEventListener(name, handler)
    },
    dispatch,
  }
}

export default {
  name: 'YouTubePlayerFrame',
  props: {
    videoId: { type: String, required: true },
  },
  emits: ['media-ready', 'media-detached', 'media-error'],
  setup(props, { emit }) {
    const playerMount = ref(null)
    const loading = ref(true)
    let player = null
    let adapter = null
    const eventTarget = new EventTarget()
    const playerState = { value: -1, ready: false, playing: false }

    async function createPlayer() {
      if (!playerMount.value || !props.videoId) return
      loading.value = true
      try {
        const YT = await loadYouTubeApi()
        player = new YT.Player(playerMount.value, {
          width: '100%',
          height: '100%',
          videoId: props.videoId,
          host: 'https://www.youtube-nocookie.com',
          playerVars: {
            controls: 0,
            disablekb: 1,
            fs: 0,
            iv_load_policy: 3,
            modestbranding: 1,
            playsinline: 1,
            rel: 0,
            origin: window.location.origin,
          },
          events: {
            onReady(event) {
              playerState.ready = true
              adapter = createMediaAdapter(event.target, eventTarget, playerState)
              loading.value = false
              emit('media-ready', adapter)
              adapter.dispatch('loadedmetadata')
              adapter.dispatch('durationchange')
            },
            onStateChange(event) {
              playerState.value = event.data
              if (event.data === YT.PlayerState.PLAYING) playerState.playing = true
              if ([YT.PlayerState.PAUSED, YT.PlayerState.ENDED, YT.PlayerState.CUED].includes(event.data)) {
                playerState.playing = false
              }
              if (!adapter) return
              if (event.data === YT.PlayerState.BUFFERING) adapter.dispatch('waiting')
              if (event.data === YT.PlayerState.PLAYING) adapter.dispatch('play')
              if (event.data === YT.PlayerState.PAUSED) adapter.dispatch('pause')
              if (event.data === YT.PlayerState.ENDED) adapter.dispatch('ended')
            },
            onPlaybackRateChange() {
              adapter?.dispatch('ratechange')
            },
            onError(event) {
              const error = new Error(`YouTube player error ${event.data}`)
              adapter?.dispatch('error', error)
              emit('media-error', error)
            },
          },
        })
      } catch (error) {
        loading.value = false
        emit('media-error', error)
      }
    }

    function destroyPlayer() {
      if (adapter) emit('media-detached', adapter)
      adapter = null
      player?.destroy?.()
      player = null
      playerState.value = -1
      playerState.ready = false
      playerState.playing = false
    }

    onMounted(createPlayer)
    onBeforeUnmount(destroyPlayer)

    watch(() => props.videoId, (nextVideoId, previousVideoId) => {
      if (!nextVideoId || nextVideoId === previousVideoId) return
      if (player?.cueVideoById) {
        player.cueVideoById(nextVideoId)
        return
      }
      destroyPlayer()
      createPlayer()
    })

    return { playerMount, loading }
  },
}
</script>

<style scoped>
.youtube-player-shell {
  position: relative;
  width: 100%;
  height: 100%;
  background: #07101d;
}

.youtube-player-mount,
.youtube-player-mount :deep(iframe) {
  display: block;
  width: 100%;
  height: 100%;
  border: 0;
  pointer-events: none;
}

.youtube-player-loading {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  color: #d9fdfb;
  background: #07101d;
  font-size: var(--font-size-sm);
}

.youtube-player-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: var(--color-brand-500);
  border-radius: 50%;
  animation: youtubePlayerSpin 0.8s linear infinite;
}

@keyframes youtubePlayerSpin {
  to { transform: rotate(360deg); }
}

@media (prefers-reduced-motion: reduce) {
  .youtube-player-spinner { animation: none; }
}
</style>
